import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { UdyogLogo } from "@/components/UdyogLogo";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  Video,
  Users,
  BarChart3,
  Sparkles,
  Settings,
  LayoutTemplate,
  LogOut,
  Plus,
} from "lucide-react";

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, id: "overview", end: true },
  { to: "/admin/classes", label: "Classes", icon: Video, id: "classes" },
  { to: "/admin/students", label: "Students", icon: Users, id: "students" },
  { to: "/admin/templates", label: "Templates", icon: LayoutTemplate, id: "templates" },
  { to: "/admin/analytics", label: "Analytics", icon: BarChart3, id: "analytics" },
  { to: "/admin/branding", label: "Branding", icon: Sparkles, id: "branding" },
  { to: "/admin/settings", label: "Settings", icon: Settings, id: "settings" },
];

export default function AdminShell() {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const name = user?.name || (user?.email || "").split("@")[0] || "there";

  const link = ({ isActive }) =>
    `w-full flex items-center gap-3 px-4 py-2.5 rounded-full text-[12.5px] transition-colors duration-200 ${
      isActive
        ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30"
        : "text-white/45 hover:text-white/85 hover:bg-white/[0.04]"
    }`;

  return (
    <div className="min-h-screen bg-[#070908] text-white relative overflow-x-hidden">
      <div className="pointer-events-none fixed -top-52 left-[10%] w-[620px] h-[620px] rounded-full bg-emerald-500/[0.08] blur-[150px]" />
      <div className="pointer-events-none fixed -bottom-64 right-[4%] w-[520px] h-[520px] rounded-full bg-emerald-400/[0.05] blur-[150px]" />

      <div className="relative z-10 flex max-w-[1600px] mx-auto">
        <aside
          className="hidden lg:flex flex-col gap-7 w-56 shrink-0 p-6 min-h-screen sticky top-0 h-screen"
          data-testid="admin-sidebar"
        >
          <Link to="/admin" data-testid="header-logo-link">
            <UdyogLogo size={26} />
          </Link>
          <nav className="space-y-1">
            {NAV.map((it) => (
              <NavLink key={it.id} to={it.to} end={it.end} className={link} data-testid={`nav-${it.id}`}>
                <it.icon className="w-4 h-4" strokeWidth={1.7} /> {it.label}
              </NavLink>
            ))}
          </nav>
          <div className="mt-auto space-y-3">
            <div className="rounded-[22px] bg-white/[0.03] ring-1 ring-white/[0.07] p-4">
              <div className="text-[9.5px] uppercase tracking-[0.22em] text-emerald-400/80 mb-1.5">
                Signed in
              </div>
              <div className="text-[13px] text-white/85 truncate" data-testid="sidebar-user-name">
                {name}
              </div>
              <div className="text-[10.5px] text-white/35 truncate" data-testid="header-user-email">
                {user?.email}
              </div>
            </div>
            <button
              onClick={logout}
              className="w-full flex items-center gap-3 px-4 py-2.5 rounded-full text-[12.5px] text-white/45 hover:text-white/85 hover:bg-white/[0.04] transition-colors duration-200"
              data-testid="sign-out-btn"
            >
              <LogOut className="w-4 h-4" strokeWidth={1.7} /> Sign out
            </button>
          </div>
        </aside>

        <main className="flex-1 min-w-0 p-5 sm:p-8 space-y-6 pb-24 lg:pb-8">
          <div className="lg:hidden flex items-center justify-between">
            <UdyogLogo size={22} />
            <div className="flex items-center gap-2">
              <Link
                to="/admin/classes"
                className="h-9 px-3 rounded-full bg-emerald-500 text-black text-[11px] font-semibold uppercase tracking-widest grid place-items-center"
                data-testid="mobile-new-class"
              >
                <Plus className="w-4 h-4" />
              </Link>
              <button
                onClick={logout}
                className="h-9 px-3 rounded-full text-white/60 hover:text-white hover:bg-white/5"
                data-testid="sign-out-btn-mobile"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>

          <Outlet key={loc.pathname} />
        </main>
      </div>

      {/* Mobile bottom nav — real sections, not filters */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-black/85 backdrop-blur-xl ring-1 ring-white/[0.08] px-2 py-2 flex justify-around"
        data-testid="admin-mobile-nav"
      >
        {NAV.slice(0, 5).map((it) => (
          <NavLink
            key={it.id}
            to={it.to}
            end={it.end}
            data-testid={`mnav-${it.id}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-3 py-1 rounded-2xl text-[9px] uppercase tracking-widest ${
                isActive ? "text-emerald-300" : "text-white/40"
              }`
            }
          >
            <it.icon className="w-[18px] h-[18px]" strokeWidth={1.8} />
            {it.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
